<template>
  <div class="ten-calendar">
    <ten-date-header
      class="ten-calendar__header"
      type="date"
      :year="year"
      :month="month"
      @btn-click="onHeaderBtnClick"
    >
      <ten-dropdown
        :width="88"
        @change="show => { yearActive = show; }"
      >
        <span :class="['ten-calendar__header-year', { active: yearActive }]">
          {{ year }}
        </span>
        <ten-dropdown-menu slot="dropdown">
          <ten-dropdown-item
            v-for="y in yearRange"
            :key="y"
            :index="y"
          >
            <div
              :class="['ten-calendar__header-year__item', { 'selected': year === y }]"
              @click="onYearSelected(y, year === y)"
            >
              {{ y }}
            </div>
          </ten-dropdown-item>
        </ten-dropdown-menu>
      </ten-dropdown>

      <ten-dropdown
        :width="66"
        @change="show => { monthActive = show; }"
      >
        <span :class="['ten-calendar__header-month', { active: monthActive }]">
          {{ month + 1 }}
        </span>
        <ten-dropdown-menu slot="dropdown">
          <ten-dropdown-item
            v-for="m in monthRange"
            :key="m"
            :index="m"
          >
            <div
              :class="['ten-calendar__header-month__item', { 'selected': month === m }]"
              @click="onMonthSelected(m, m === month)"
            >
              {{ m + 1 }}
            </div>
          </ten-dropdown-item>
        </ten-dropdown-menu>
      </ten-dropdown>
    </ten-date-header>
    <div class="ten-calendar__body">
      <ten-date-table
        class="ten-calendar__body-table"
        :data="weeks"
        :first-day-of-week="firstDayOfWeek"
        :day-names="dayNames"
      >
        <template #default="w">
          <div @click="onDateSelected(w.value, w.active)">
            <!-- @slot
              自定义渲染整个日期单元格。
              scoped slot 参数：
              date 单元格代表的日期；
              data { type, isSelected }，type 表示该日期的所属月份，可选值有 prev-month，current-month，next-month；isSelected 标明该日期是否被选中；
            -->
            <slot
              name="fullDateCell"
              v-bind="{ date: w.value, data: { isSelected: w.active, type: w.type } }"
            >
              <div class="ten-calendar-date-cell__wrapper">
                <div class="ten-calendar-date-cell__header">
                  <span class="ten-calendar-date-cell__header-value">{{ w.value.getDate() }}</span>
                </div>
                <div class="ten-calendar-date-cell__content">
                  <!-- @slot
                    自定义渲染日期单元格内容部分。
                    scoped slot 参数：
                    date 单元格代表的日期；
                    data { type, isSelected }，type 表示该日期的所属月份，
                    可选值有 prev-month，current-month，next-month；isSelected 标明该日期是否被选中；
                  -->
                  <slot
                    name="dateCell"
                    v-bind="{ date: w.value, data: { isSelected: w.active, type: w.type } }"
                  />
                </div>
              </div>
            </slot>
          </div>
        </template>
      </ten-date-table>
    </div>
  </div>
</template>

<script>
import TenDropdown from '../dropdown';
import TenDateHeader from '../date-picker/basic/header';
import TenDateTable from '../date-picker/basic/table';
import { range } from '../scripts/utils/numbers';
import { getToday, flagActive, getWeeks, addMonth, subtractMonth } from '../date-picker/util';
import { isDate } from '../scripts/utils/helper';
const TenDropdownMenu = TenDropdown.DropdownMenu;
const TenDropdownItem = TenDropdown.DropdownItem;

const name = 'ten-calendar';

function validateDate(date) {
  if (typeof date === 'string') date = new Date(date);
  return isDate(date) ? date : getToday();
}

const DAY_NAMES = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];

export default {
  name,
  components: {
    TenDateTable,
    TenDateHeader,
    TenDropdown,
    TenDropdownMenu,
    TenDropdownItem,
  },
  props: {
    /**
     * @model
     * 展示日期
     */
    value: {
      type: [Date, String],
    },
    /**
     * 默认展示日期
     */
    defaultValue: {
      type: [Date, String],
    },
    /**
     * 周起始日，默认周一。
     * 0~6对应周日到周六
     */
    firstDayOfWeek: {
      type: Number,
      default: 1,
      validator: v => v >= 0 && v <= 6,
    },
  },
  data() {
    return {
      dataValue: validateDate(this.value),
      yearActive: false,
      monthActive: false,
      dayNames: DAY_NAMES,
    };
  },
  computed: {
    year() {
      return this.dataValue.getFullYear();
    },
    month() {
      return this.dataValue.getMonth();
    },
    minYear() {
      return this.year - 10;
    },
    maxYear() {
      return this.year + 10;
    },
    minMonth() {
      return 0;
    },
    maxMonth() {
      return 11;
    },
    weeks() {
      const { year, month, firstDayOfWeek } = this;
      return flagActive(getWeeks({ year, month }, { firstDayOfWeek }), { start: this.dataValue });
    },
    yearRange() {
      return range(this.maxYear, this.minYear);
    },
    monthRange() {
      return range(this.maxMonth, this.minMonth);
    },
  },
  watch: {
    value: {
      handler(value) {
        this.dataValue = validateDate(value);
      },
      immediate: true,
    },
  },
  methods: {
    onHeaderBtnClick(btnType, flag) {
      const monthCount = btnType === 'double' ? 12 : 1;
      let next;

      if (flag === 1) {
        next = addMonth(this.dataValue, monthCount);
      } else {
        next = subtractMonth(this.dataValue, monthCount);
      }

      this.$emit('change', next);
    },
    onYearSelected(year, isSelected) {
      if (isSelected) return;
      this.$emit('change', new Date(year, this.dataValue.getMonth(), this.dataValue.getDate()));
    },
    onMonthSelected(month, isSelected) {
      if (isSelected) return;
      this.$emit('change', new Date(this.dataValue.getFullYear(), month, this.dataValue.getDate()));
    },
    onDateSelected(date, isSelected) {
      if (isSelected) return;
      /**
       * 日期变化回调
       *
       * @param {Date} date 日期
       */
      this.$emit('change', date);
      /**
       * 选择日期回调
       *
       * @param {Date} date 日期
       */
      this.$emit('select', date);
    },
  },
};
</script>
